import service from './http'
import serve from "@/utils/serve";
import {AccessLevel, Member} from "@/types/AccessLevel";
import {Person} from "@/types/Person";

/**
 * As pessoas de um ambiente, com o nível de acesso e os papéis de cada uma.
 *
 * Sub-recurso do ambiente, e não um campo do `PATCH /systems/:alias`: assim o portão do servidor
 * já sabe, pelo caminho, que mexer aqui exige administrador — e o `users` continua fora do que o
 * ambiente devolve a qualquer membro.
 *
 * Cada pessoa é apontada pelo id da conta (`user`), e não pelo e-mail, que muda. Só o convite vai
 * pelo e-mail — é o que quem convida conhece —, e volta com o id que a conta tem (ou ganhou).
 */
class MemberService {
    async list({systemAlias}: { systemAlias: string }): Promise<Member[]> {
        return await serve(async () => await service.get(`systems/${systemAlias}/users`))
    }

    /**
     * As pessoas que se pode convidar de uma lista: as contas do lunedi, sem as pendentes e sem
     * quem chama. Fora de `systems/` porque não é de um ambiente.
     */
    async known(): Promise<Person[]> {
        return await serve(async () => await service.get('people'))
    }

    /** Devolve a pessoa como ficou GUARDADA — com o id da conta, e o e-mail aparado e minúsculo. */
    async add({systemAlias}: { systemAlias: string }, invite: { email: string, accessLevel: AccessLevel }): Promise<Member> {
        return await serve(async () => await service.post(`systems/${systemAlias}/users`, invite))
    }

    /** O nível, os papéis, ou os dois — e volta a pessoa como ficou. */
    async update({systemAlias, user}: { systemAlias: string, user: number },
                 changes: { accessLevel?: AccessLevel, roles?: string[] }): Promise<Member> {
        return await serve(async () => await service.patch(this.url(systemAlias, user), changes))
    }

    async remove({systemAlias, user}: { systemAlias: string, user: number }) {
        return await serve(async () => await service.delete(this.url(systemAlias, user)))
    }

    private url(systemAlias: string, user: number) {
        return `systems/${systemAlias}/users/${user}`
    }
}

export default MemberService
